import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ArrowUpRight, PieChart, AlertTriangle } from 'lucide-react';

export default function ValueSection() {
  const { t } = useLanguage();
  const ref = useScrollReveal();

  return (
    <section id="value" data-testid="value-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-[#0B1E2E]" />

      <div ref={ref} className="reveal relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-sm uppercase tracking-widest text-[#42D2D9] font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('value.caption')}
        </p>
        <div className="section-line mb-8" />

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-16 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('value.title')}
        </h2>

        {/* Price comparison */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* Entry Price */}
          <div data-testid="value-entry-price" className="glass-card p-8 rounded-lg text-center">
            <p className="text-sm text-slate-400 mb-4 uppercase tracking-widest">{t('value.entry_label')}</p>
            <div className="metric-value text-5xl md:text-6xl mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('value.entry_value')}
            </div>
            <p className="text-sm text-slate-500">{t('value.entry_unit')}</p>
          </div>

          {/* Market Value */}
          <div data-testid="value-market-price" className="glass-card p-8 rounded-lg text-center">
            <p className="text-sm text-slate-400 mb-4 uppercase tracking-widest">{t('value.market_label')}</p>
            <div className="text-5xl md:text-6xl font-extrabold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('value.market_value')}
            </div>
            <p className="text-sm text-slate-500">{t('value.market_unit')}</p>
          </div>

          {/* Potential */}
          <div data-testid="value-potential" className="glass-card p-8 rounded-lg text-center border-[#42D2D9]/30">
            <p className="text-sm text-slate-400 mb-4 uppercase tracking-widest">{t('value.potential')}</p>
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="metric-value text-5xl md:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('value.potential_value')}
              </span>
              <ArrowUpRight className="w-8 h-8 text-[#42D2D9]" />
            </div>
            <p className="text-sm text-slate-500">&nbsp;</p>
          </div>
        </div>

        {/* Disclaimer */}
        <div data-testid="value-disclaimer" className="flex items-start gap-3 mb-16 p-4 rounded border border-[#FBCB09]/20 bg-[#FBCB09]/5">
          <AlertTriangle className="w-5 h-5 text-[#FBCB09] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400 leading-relaxed">
            {t('value.disclaimer')}
          </p>
        </div>

        {/* Portfolio Strategy */}
        <div className="glass-card p-8 md:p-12 rounded-lg grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <PieChart className="w-6 h-6 text-[#42D2D9]" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('value.portfolio_title')}
              </h3>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t('value.portfolio_desc')}
            </p>
          </div>

          {/* Visual portfolio allocation */}
          <div className="flex items-center justify-center">
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12" />
                <circle
                  cx="50" cy="50" r="40" fill="none"
                  stroke="#42D2D9" strokeWidth="12"
                  strokeDasharray={`${0.15 * 2 * Math.PI * 40} ${2 * Math.PI * 40}`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="metric-value text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>10-20%</span>
                <span className="text-xs text-slate-500 mt-1">Plastic Credits</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
